import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFeatureEnabled } from "@/lib/features";
import { notify } from "@/lib/notify";

import { authApi } from "@/lib/api/auth";
import { branchesApi } from "@/lib/api/branches";
import { qk } from "@/lib/query-client";
import { useAuthStore } from "@/stores/auth-store";
import { exitWorkspace } from "@/lib/workspace";
import { initials } from "@/lib/utils/format";
import { cn } from "@/lib/utils/cn";
import { useMountTransition } from "@/lib/utils/useMountTransition";
import { useImageFallback } from "@/lib/utils/useImageFallback";
import { Icon } from "./Icon";
import { NotificationBell } from "./NotificationBell";
import { QuickNavSearch } from "./QuickNavSearch";

// Multi-Branch Tenant Admin: every query key prefix that reads
// branch-scoped data, invalidated on a branch switch so no stale
// previously-selected-branch rows can linger in a cached list a switched-to
// page happens to re-render before its own refetch lands. A broader sweep
// than any single mutation's own invalidation, deliberately - this is a
// context change, not a single resource's update.
const BRANCH_SCOPED_QUERY_PREFIXES = [
  "employees", "departments", "attendance", "leaves", "leaveBalances",
  "payroll", "biometric", "reports",
];

export function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const user = useAuthStore((s) => s.user);
  // Falls back to the initials monogram when the avatar cannot be loaded,
  // rather than rendering the browser's broken-image glyph. Keyed on the URL
  // so uploading a new picture is retried instead of staying on the fallback.
  const { showImage: showAvatar, onError: onAvatarError } = useImageFallback(user?.avatar_url);
  const signOut = useAuthStore((s) => s.signOut);
  const impersonatedTenant = useAuthStore((s) => s.impersonatedTenant);
  const activeBranch = useAuthStore((s) => s.activeBranch);
  const setActiveBranch = useAuthStore((s) => s.setActiveBranch);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [menuOpen, setMenuOpen] = useState(false);
  const [branchMenuOpen, setBranchMenuOpen] = useState(false);
  const { shouldRender: menuShouldRender, isVisible: menuVisible } = useMountTransition(menuOpen, 150);
  const { shouldRender: branchMenuShouldRender, isVisible: branchMenuVisible } = useMountTransition(branchMenuOpen, 150);

  const isTenantAdmin = user?.role === "tenant_admin" && !user.is_super_admin;
  const branchesEnabled = useFeatureEnabled("branch_management");
  const { data: branchesPage } = useQuery({
    queryKey: qk.branches.list({ status: "active", per_page: 100 }),
    queryFn: () => branchesApi.list({ status: "active", per_page: 100 }),
    enabled: isTenantAdmin && branchesEnabled && branchMenuOpen,
  });
  const branches = branchesPage?.items ?? [];

  function switchBranch(branchId: string) {
    const branch = branches.find((b) => b.id === branchId);
    if (!branch || branch.id === activeBranch?.id) {
      setBranchMenuOpen(false);
      return;
    }
    setActiveBranch(branch);
    setBranchMenuOpen(false);
    for (const prefix of BRANCH_SCOPED_QUERY_PREFIXES) {
      queryClient.invalidateQueries({ queryKey: [prefix] });
    }
    notify.success(`Switched to ${branch.name}`);
  }

  const logoutMutation = useMutation({
    // The local session is cleared regardless of whether the API call
    // itself succeeds (a network blip shouldn't trap someone in a
    // "logged in" state) - onSettled, not onSuccess, and no error toast
    // needed either way, since the person is leaving the app right now.
    meta: { silent: true },
    mutationFn: authApi.logout,
    onSettled: () => {
      notify.info("Signed out successfully.");
      // Clearing the query cache, not just the auth store, matters on a
      // shared machine: without it, the next person to sign in on this tab
      // could briefly see the previous user's cached queries re-render
      // before their own requests land.
      signOut();
      queryClient.clear();
      navigate("/login", { replace: true });
    },
  });

  return (
    <header className="sticky top-0 z-20 flex h-16 items-center justify-between gap-3 border-b border-slate-200 bg-white/90 px-4 backdrop-blur sm:px-6">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <button
          onClick={onMenuClick}
          className="shrink-0 rounded-lg p-2 text-slate-500 transition-all duration-200 ease-out hover:scale-105 hover:bg-primary-50 hover:text-primary-700 lg:hidden"
          aria-label="Toggle menu"
        >
          <Icon name="menu" className="h-5 w-5" />
        </button>
        <QuickNavSearch />
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {impersonatedTenant && (
          <button
            onClick={() => {
              exitWorkspace();
              navigate("/platform/tenants");
            }}
            className="hidden items-center gap-1.5 rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-800 sm:flex"
          >
            <Icon name="layers" className="h-3.5 w-3.5" />
            Managing {impersonatedTenant.name} · Exit to Workspaces
          </button>
        )}

        {isTenantAdmin && branchesEnabled && activeBranch && (
          <div className="relative hidden sm:block">
            <button
              onClick={() => setBranchMenuOpen((v) => !v)}
              data-testid="branch-switcher-trigger"
              className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-medium text-slate-700 transition-colors duration-200 ease-out hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
            >
              <Icon name="building" className="h-3.5 w-3.5" />
              {activeBranch.name}
              <Icon name="chevron-down" className="h-3.5 w-3.5 text-slate-400" />
            </button>

            {branchMenuShouldRender && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setBranchMenuOpen(false)} />
                <div
                  className={cn(
                    "absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-lg border border-slate-200 bg-white py-1 shadow-lg transition-all duration-150 ease-out",
                    branchMenuVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
                  )}
                >
                  <div className="border-b border-slate-100 px-3 py-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Switch Branch</p>
                  </div>
                  {branches.length === 0 ? (
                    <p className="px-3 py-2 text-sm text-slate-400">Loading branches…</p>
                  ) : (
                    branches.map((b) => (
                      <button
                        key={b.id}
                        onClick={() => switchBranch(b.id)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 transition-colors duration-200 ease-out hover:bg-primary-50/60 hover:text-primary-800"
                      >
                        <Icon
                          name="check-circle"
                          className={cn("h-3.5 w-3.5 shrink-0", b.id === activeBranch.id ? "text-primary-700" : "text-transparent")}
                        />
                        <span className="truncate">{b.name}</span>
                      </button>
                    ))
                  )}
                  <div className="border-t border-slate-100" />
                  <button
                    onClick={() => {
                      setBranchMenuOpen(false);
                      navigate("/tenant/branch-selection");
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition-colors duration-200 ease-out hover:bg-primary-50/60 hover:text-primary-800"
                  >
                    <Icon name="layers" className="h-4 w-4" />
                    View all branches
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {!user?.is_super_admin && <NotificationBell />}

        <div className="relative">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            data-testid="user-menu-trigger"
            className="flex items-center gap-2 rounded-lg px-2 py-1.5 transition-colors duration-200 ease-out hover:bg-primary-50"
          >
            {showAvatar && user?.avatar_url ? (
              <img
                src={user.avatar_url}
                alt=""
                className="h-9 w-9 rounded-full object-cover ring-2 ring-white"
                onError={onAvatarError}
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-800 ring-2 ring-white">
                {initials(user?.full_name ?? user?.email ?? "?")}
              </div>
            )}
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium leading-tight text-slate-800">{user?.full_name}</p>
              <p className="text-xs leading-tight text-slate-500">{user?.role_name}</p>
            </div>
            <Icon name="chevron-down" className="h-4 w-4 text-slate-400" />
          </button>

          {menuShouldRender && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
              <div
                className={cn(
                  "absolute right-0 z-20 mt-2 w-52 origin-top-right rounded-lg border border-slate-200 bg-white py-1 shadow-lg transition-all duration-150 ease-out",
                  menuVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0",
                )}
              >
                <div className="border-b border-slate-100 px-3 py-2">
                  <p className="truncate text-sm font-medium text-slate-800">{user?.email}</p>
                </div>
                {/* Every profile/account/security page now lives in the
                    Settings module (also reachable from the sidebar) - this
                    is a shortcut into it, not a second copy of its content. */}
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    navigate("/settings");
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition-colors duration-200 ease-out hover:bg-primary-50/60 hover:text-primary-800"
                >
                  <Icon name="settings" className="h-4 w-4" />
                  Settings
                </button>
                <div className="border-t border-slate-100" />
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    logoutMutation.mutate();
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-600 transition-colors duration-200 ease-out hover:bg-primary-50/60 hover:text-primary-800"
                >
                  <Icon name="logout" className="h-4 w-4" />
                  Sign out
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
